import { Box, Flex, UseToastOptions } from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';
import { kebabCase, sortBy } from 'lodash';
import { UseFormSetValue } from 'react-hook-form';

import { ArrowRightIcon } from '@/components/common/Icons/ArrowRightIcon';
import { NextTFunction } from '@/i18n/client';
import { ResponseBody } from '@/typings/common';
import { FugitiveCategoryType, getFugitiveCategoryType } from '@/utils/emission';

import { SimpleUser } from '../../../../account-management/typings';
import CreateEmissionConstants from '../../../src/constants/CreateEmissionConstants';
import { ActivityUnit } from '../../../src/enums/ActivityDataTypeEnum';
import {
  EmissionSourceFactorSource,
  EmissionSourceFactorUnitType,
  EmissionSourceGreenHouseGases,
  EmissionSourceSubCategory,
} from '../../../src/enums/EmissionSourceEnum';
import { FugitiveType } from '../../../src/enums/FugitiveEmissionEnum';
import {
  EmissionDeleteError,
  EmissionDeleteResponse,
  EmissionDeleteSuccess,
  RawMaterialOption,
} from '../../../src/typings/EmissionSourceTypings';
import { DEFAULT_GAS, RAW_MATERIALS_DROPDOWN_OPTIONS_ORDER } from '../constants';
import { CreateEmissionType } from '../create/formValidation';
import ActivityCount from './ActivityCount';

export const getInitialFactors = (subCategory: EmissionSourceSubCategory): CreateEmissionType['factors'] => {
  if (!Array.isArray(CreateEmissionConstants.CATEGORY_ONE_DEFAULT_VALUES?.factors)) {
    return [];
  }

  switch (subCategory) {
    case EmissionSourceSubCategory.CAT_4_1B:
      CreateEmissionConstants.CATEGORY_ONE_DEFAULT_VALUES.factors?.[0]?.gasData?.map((gas) => {
        if (gas.gasKey === DEFAULT_GAS) {
          return {
            ...gas,
            factorUnitType: EmissionSourceFactorUnitType.ENERGY_KWH,
            factorUnit: ActivityUnit.KWH,
            ghgUnit: ActivityUnit.KG,
          };
        }
        return gas;
      });
      return [
        ...CreateEmissionConstants.CATEGORY_ONE_DEFAULT_VALUES.factors,
        {
          ...CreateEmissionConstants.CATEGORY_ONE_DEFAULT_VALUES.factors[0],
          factorSourceType: EmissionSourceFactorSource.TDLOSS,
        },
      ];
    case EmissionSourceSubCategory.CAT_4_3:
      return [
        {
          ...CreateEmissionConstants.CATEGORY_ONE_DEFAULT_VALUES.factors[0],
          factorSourceType: EmissionSourceFactorSource.TREATMENT,
        },
      ];
    default:
      return CreateEmissionConstants.CATEGORY_ONE_DEFAULT_VALUES.factors;
  }
};

export const getFactor = (emission: CreateEmissionType, factorSourceType = EmissionSourceFactorSource.DEFAULT) => {
  return emission.factors?.find((factor) => factor.factorSourceType === factorSourceType);
};

export const getDefaultFactorSourceType = (subCategory: EmissionSourceSubCategory) => {
  switch (subCategory) {
    case EmissionSourceSubCategory.CAT_4_3:
      return EmissionSourceFactorSource.TREATMENT;
    default:
      return EmissionSourceFactorSource.DEFAULT;
  }
};

const emissionGroupDefaultTableColumns = ({
  redirectToDataCollection,
  t,
}: {
  redirectToDataCollection?: (emissionGroupId: string) => void;
  t: NextTFunction;
}): ColumnDef<Record<string, unknown>>[] => {
  const columns: ColumnDef<Record<string, unknown>>[] = [
    {
      id: kebabCase('ghgProducedType'),
      header: t('org-cfv-create-emission.ghgProducedType'),
      accessorKey: 'ghgProducedType',
      cell: () => <>{t('ghg.CO2')}</>,
    },
    {
      id: kebabCase('personInChargeEmission'),
      header: t('org-cfv-list-create-project.personInCharge'),
      accessorKey: 'personInChargeEmission',
      cell: ({ row: { original } }) => (
        <>{sortBy((original.assignedUsers as SimpleUser[]).map((item) => item.name)).join(', ')}</>
      ),
    },
  ];

  if (typeof redirectToDataCollection === 'function') {
    columns.push({
      id: kebabCase('noOfActivity'),
      header: t('org-cfv-create-emission.noOfActivity'),
      accessorKey: 'noOfActivity',
      cell: ({ row: { original } }) => {
        return (
          <Flex align={'center'} justify={'space-between'}>
            <ActivityCount
              createdActivities={original.createdActivities as number}
              approvedActivities={original.numberOfActivities as number}
            />
            <ArrowRightIcon cursor={'pointer'} onClick={() => redirectToDataCollection(original?.id as string)} />
          </Flex>
        );
      },
    });
  }

  return columns;
};

// 3.1 / 4.1
export const getRawMaterialAcquisitionTableColumns = ({
  redirectToDataCollection,
  t,
}: {
  redirectToDataCollection?: (emissionGroupId: string) => void;
  t: NextTFunction;
}): ColumnDef<Record<string, unknown>>[] => [
  {
    header: t('common.emission'),
    cell: ({ row: { original } }) => <>{t(`fd-raw-material.emission-${original.name}`)}</>,
  },
  ...emissionGroupDefaultTableColumns({ redirectToDataCollection, t }),
];

// 3.2 / 5.1 / 5.3
export const getDistributionTransportationTableColumns = ({
  redirectToDataCollection,
  t,
}: {
  redirectToDataCollection?: (emissionGroupId: string) => void;
  t: NextTFunction;
}): ColumnDef<Record<string, unknown>>[] => [
  {
    header: t('common.emission'),
    accessorKey: 'name',
  },
  ...emissionGroupDefaultTableColumns({ redirectToDataCollection, t }),
];

export const getCommutingCategoryTableColumns = ({
  redirectToDataCollection,
  t,
}: {
  redirectToDataCollection?: (emissionGroupId: string) => void;
  t: NextTFunction;
}): ColumnDef<Record<string, unknown>>[] => [
  {
    id: kebabCase('emissionKind'),
    header: t('common.emission'),
    accessorKey: 'emissionKind',
    cell: () => t('org-cfv-create-emission.CAT_3_3'),
  },
  ...emissionGroupDefaultTableColumns({ redirectToDataCollection, t }),
];

const mappingEmissionNameAndErrorMsg = ({
  success,
  emissionIdToNameMap,
  emissionDeleteResponses,
  t,
}:
  | {
      success: true;
      emissionIdToNameMap: { [id: string]: string };
      emissionDeleteResponses: EmissionDeleteSuccess[];
      t: NextTFunction;
    }
  | {
      success: false;
      emissionIdToNameMap: { [id: string]: string };
      emissionDeleteResponses: EmissionDeleteError[];
      t: NextTFunction;
    }): string => {
  if (success) {
    return emissionDeleteResponses
      .map((emissionDeleteRes) => emissionIdToNameMap[emissionDeleteRes.id] || '')
      .join(', ');
  }

  return emissionDeleteResponses
    .map((emissionDeleteRes) => {
      const name = emissionIdToNameMap[emissionDeleteRes.id] || '';
      if (success) {
        return name;
      }
      const errMsg = emissionDeleteRes.code ? t(`error-code.${emissionDeleteRes.code.toString()}`) : '';
      return `${name} ${errMsg}`;
    })
    .join(', ');
};

export const handleBulkDeleteEmissionError = (
  emissionIdToNameMap: Record<string, string>,
  response: ResponseBody<EmissionDeleteResponse>,
  t: NextTFunction,
): UseToastOptions => {
  const { deletedEmissionSources, failedEmissionSources } = response.data;
  const countSucceed = deletedEmissionSources?.length || 0;
  const countFailed = failedEmissionSources?.length || 0;
  const successDesc = `${t('common.deleted')}: ${mappingEmissionNameAndErrorMsg({
    success: true,
    emissionIdToNameMap,
    emissionDeleteResponses: deletedEmissionSources,
    t,
  })}`;
  const failedDesc = `${t('common.error')}: ${mappingEmissionNameAndErrorMsg({
    success: false,
    emissionIdToNameMap,
    emissionDeleteResponses: failedEmissionSources,
    t,
  })}`;

  const DELETE_EMISSION_ERROR_TOAST_SETTING: UseToastOptions = {
    title: t('org-cfv-create-emission.deleteEmission'),
    isClosable: true,
    position: 'top-right',
  };

  if (!countFailed) {
    return { ...DELETE_EMISSION_ERROR_TOAST_SETTING, description: successDesc, status: 'success' };
  }

  if (!countSucceed) {
    return { ...DELETE_EMISSION_ERROR_TOAST_SETTING, description: failedDesc, status: 'error' };
  }

  return {
    ...DELETE_EMISSION_ERROR_TOAST_SETTING,
    description: (
      <Box>
        <Box>{successDesc}</Box>
        <Box>{failedDesc}</Box>
      </Box>
    ),
    status: 'warning',
    duration: 9000,
  };
};

export const showEmissionFactorsCustomUnitForm = (
  showCustomizedFields: boolean | null,
  emission: CreateEmissionType,
): boolean => {
  const fugitiveCategoryType = getFugitiveCategoryType(emission?.metadata?.fugitiveCategory);
  if (fugitiveCategoryType === FugitiveCategoryType.WASTEWATER) {
    return emission.metadata?.fugitiveType === FugitiveType.FT_OTHER;
  }

  return Boolean(showCustomizedFields);
};

export const clearFactor = (setValue: UseFormSetValue<CreateEmissionType>, factors?: CreateEmissionType['factors']) => {
  if (factors && factors.length > 0) {
    type GasDataType = (typeof factors)[0]['gasData'];

    const gases = factors[0].greenHouseEmissionType;
    const defaultGhgData = gases
      .filter((g) => g !== EmissionSourceGreenHouseGases.NA)
      .reduce((acc, gas) => {
        const defaultGhg = {
          factor: 0,
          factorUnit: undefined as unknown as ActivityUnit,
          factorUnitType: undefined as unknown as EmissionSourceFactorUnitType,
          gasKey: gas,
          gasName: '',
          ghgUnit: undefined as unknown as ActivityUnit,
          isRefrigerant: false,
          isPFCS: false,
          isCO2Equivalent: false,
        };
        return acc ? [...acc, defaultGhg] : [defaultGhg];
      }, [] as GasDataType);
    setValue('factors.0.gasData', defaultGhgData);
  }
};

export const getIdxWithGhg = (factors: CreateEmissionType['factors'], ghg: EmissionSourceGreenHouseGases): number =>
  factors?.[0]?.gasData?.findIndex((gas) => gas?.gasKey === ghg) ?? -1;

export const getGasDataWithGhg = (factors: CreateEmissionType['factors'], ghg: EmissionSourceGreenHouseGases) => {
  const gasIndex = getIdxWithGhg(factors, ghg);
  return gasIndex >= 0 ? factors?.[0]?.gasData?.[gasIndex] : undefined;
};

export const getFirstGasData = (
  factors: CreateEmissionType['factors'],
  greenHouseEmissionType?: EmissionSourceGreenHouseGases[],
) => {
  const gasKey = greenHouseEmissionType?.[0] || DEFAULT_GAS;
  return getGasDataWithGhg(factors, gasKey);
};

export const rawMaterial1Sorter = (a: RawMaterialOption, b: RawMaterialOption) => {
  let aIndex = RAW_MATERIALS_DROPDOWN_OPTIONS_ORDER.findIndex((value) => value === a.rawMaterialType);
  let bIndex = RAW_MATERIALS_DROPDOWN_OPTIONS_ORDER.findIndex((value) => value === b.rawMaterialType);

  // move unknown to the end
  if (aIndex === -1) {
    aIndex = Number.MAX_SAFE_INTEGER;
  }
  if (bIndex === -1) {
    bIndex = Number.MAX_SAFE_INTEGER;
  }

  return aIndex - bIndex;
};
